import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { get } from 'lodash-es';
/* Components */
import { DigitalRetailPriceCheckerLeadForm } from 'client/site-modules/shared/components/lead-form/unified-lead-form-pages/digital-retail-price-checker-lead-form';
import { VdpPricingChartConnected } from 'site-modules/shared/components/vdp-pricing-chart/vdp-pricing-chart-connected';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
/* Models */
import { VisitorModel } from 'client/data/models/visitor';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
/* Utils */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { getVinPath } from 'site-modules/shared/utils/inventory/vdp-utils';
import { isTmvPriceAvailable } from 'site-modules/shared/utils/inventory/price-analysis';
import { isMakePorsche } from 'site-modules/shared/utils/inventory/make';
import { getEmbeddedLeadFormCta } from 'site-modules/shared/components/inventory/vdp-embedded-lead-form/vdp-embedded-lead-form';
import { getVdpCtas } from 'site-modules/shared/utils/inventory/get-vdp-ctas';
/* Constants */
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { PRICE_CHECKER_NAME } from 'site-modules/shared/constants/price-checker';
import { isLeaseEnabled } from 'site-modules/shared/utils/inventory-utils/is-lease-enabled';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

import './vdp-price-checker.scss';

export function VdpPriceCheckerUI({
  vin,
  zipCode,
  pageName = '',
  stockNumber = null,
  isMobile = false,
  isNewVin = false,
  showLeasePayments = false,
  downPayment = 0,
  term = 0,
  isLeasePaymentEnabled = false,
  make = null,
  model = null,
  year = null,
  tmvAPIoptions = {},
  radius = 50,
  isLeaseView = false,
  forceLease = false,
  hideLeadForm = false,
  partnerCode = undefined,
  vinData,
  defaultLoanTerm72mo = false,
  location = {},
  isPurchase = false,
}) {
  const params = {
    vin,
    zipCode,
    radius,
    partnerCode,
    make,
    type: vinData?.type,
    isLease: isLeaseEnabled(location),
  };

  const creativeId = getLeadFormCreativeId({
    isNewVin,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.DEAL_CHECK_PRICE_SINGLE_EMBEDDED,
  });

  const options = {
    showLeasePayments,
    isUsed: !isNewVin,
    creativeId,
    tmvAPIoptions,
  };

  const showLeadForm = () => {
    if (isNewVin && isMakePorsche(make)) {
      return false;
    }

    return !!getEmbeddedLeadFormCta(getVdpCtas({ vinData, location })) && !hideLeadForm;
  };

  const withLeadForm = showLeadForm();

  const isShop5146NciVdp = isNewVin && pageName === 'new_model_car_inventory_vin_detail';
  const experimentRecipe = useSelector(state =>
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: isShop5146NciVdp ? 'shop-5146-nci-copy' : 'shop-5148-uci-copy',
      defaultVal: 'ctrl',
    })
  );
  const getLeadFormOptions = recipe => ({
    ...options,
    ...(isShop5146NciVdp &&
      recipe === 'chal' && {
        hidePricingTooltip: true,
        headerText: 'Ask the dealer about rebates and incentives',
        subHeaderText: 'Use this form to contact the dealer about available specials.',
        submitButtonText: 'Request Information',
      }),
    ...(!isNewVin &&
      recipe === 'chal' && {
        headerText: 'Contact the dealer about this vehicle’s price',
        submitButtonText: 'Contact Dealer',
      }),
  });
  const leadFormOptions = getLeadFormOptions(experimentRecipe);

  const isTmvAvailable = isTmvPriceAvailable(vinData);

  return (
    <ScrollElement id={PRICE_CHECKER_NAME}>
      <section
        className="vdp-price-checker mb-md-0"
        data-tracking-parent={isNewVin ? 'new-deal_check_embedded' : 'used-deal_check_embedded'}
      >
        <span className="medium text-gray-darker">
          {isTmvAvailable && (stockNumber ? `Stock#: ${stockNumber}` : `VIN: ${vin}`)}
          {showLeasePayments &&
            isTmvAvailable &&
            (!!term || !!downPayment) &&
            `, ${term} month term, ${formatPriceString(downPayment)} down`}
          {!isTmvAvailable &&
            `This chart estimates pricing (before taxes and fees) for a similarly equipped New ${year} ${make} ${model}. Contact dealer for price.`}
        </span>
        <VdpPricingChartConnected
          vin={vin}
          vinData={vinData}
          isMobile={isMobile}
          isUsed={!isNewVin}
          customConfig={{ tmvAPIoptions }}
          showLeasePayments={showLeasePayments}
          isLeasePaymentFocused={showLeasePayments}
          skipWidgetViewTracking
          isPurchaseOnly={!showLeasePayments}
          isLeasePaymentEnabled={isLeasePaymentEnabled}
          useGraphQlForLoanOffers={isNewVin}
          isLeaseView={isLeaseView}
          forceLease={forceLease}
          withShadow={withLeadForm}
          defaultLoanTerm72mo={defaultLoanTerm72mo}
          isPurchase={isPurchase}
        />
        {withLeadForm && (
          <DigitalRetailPriceCheckerLeadForm
            params={params}
            options={leadFormOptions}
            useGraphQlForLoanOffers={isNewVin}
          />
        )}
      </section>
    </ScrollElement>
  );
}

VdpPriceCheckerUI.propTypes = {
  vin: PropTypes.string.isRequired,
  vinData: PropTypes.oneOfType([
    InventoryEntities.InventoryVin,
    PropTypes.shape({
      prices: PropTypes.shape({
        displayPrice: PropTypes.number,
      }),
    }),
  ]).isRequired,
  zipCode: PropTypes.string.isRequired,
  pageName: PropTypes.string,
  stockNumber: PropTypes.string,
  isMobile: PropTypes.bool,
  isNewVin: PropTypes.bool,
  showLeasePayments: PropTypes.bool,
  downPayment: PropTypes.number,
  term: PropTypes.number,
  isLeasePaymentEnabled: PropTypes.bool,
  make: PropTypes.string,
  model: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tmvAPIoptions: PropTypes.shape({}),
  radius: PropTypes.number,
  isLeaseView: PropTypes.bool,
  forceLease: PropTypes.bool,
  hideLeadForm: PropTypes.bool,
  partnerCode: PropTypes.string,
  defaultLoanTerm72mo: PropTypes.bool,
  location: PropTypes.shape({}),
  isPurchase: PropTypes.bool,
};

const stateToPropsConfig = {
  zipCode: bindToPath('location.zipCode', VisitorModel),
  vinData: bindToPath(({ vin }) => getVinPath(vin), InventoryModel, null, false),
};

export const mapStateToProps = state => ({
  pageName: get(state, 'pageContext.legacy.pageName'),
  defaultLoanTerm72mo: !!state.featureFlags?.defaultLoanTerm72mo,
  location: get(state, 'pageContext.location'),
});

export const VdpPriceChecker = connect(mapStateToProps)(connectToModel(VdpPriceCheckerUI, stateToPropsConfig));
